import { z } from "zod";

import SelectFormGroup from "../../FormGroups/SelectFormGroup.jsx";
import TextFormGroup from "../../FormGroups/TextFormGroup.jsx";

const numberOrEmpty = (opts = {}) =>
    z.preprocess(
        (val) => {
            if (val === "" || val === null || val === undefined)
                return undefined;
            return Number(val);
        },
        opts.nonnegative
            ? z
                  .number({ invalid_type_error: "Please enter a valid number" })
                  .nonnegative()
                  .optional()
            : z
                  .number({ invalid_type_error: "Please enter a valid number" })
                  .optional(),
    );

const resume_interrupted_line_scan = z.preprocess((v) => {
    if (v === "true") return true;
    if (v === "false") return false;
    if (v === "undefined") return undefined;
    return v;
}, z.boolean().optional());

export const RouteplannerSettingsSchema = z.object({
    routeplanner: z
        .object({
            resume_interrupted_line_scan: resume_interrupted_line_scan,
            horizontal_overlap: numberOrEmpty(),
            vertical_overlap: numberOrEmpty(),
            working_distance: numberOrEmpty({ nonnegative: true }),
        })
        .optional(),
});

export const RouteplannerSettingsValues = (settings) => {
    const resumeValue = settings?.routeplanner?.resume_interrupted_line_scan;

    return {
        routeplanner: {
            resume_interrupted_line_scan:
                resumeValue === true
                    ? "true"
                    : resumeValue === false
                      ? "false"
                      : "undefined",
            horizontal_overlap:
                settings?.routeplanner?.horizontal_overlap ?? "",
            vertical_overlap: settings?.routeplanner?.vertical_overlap ?? "",
            working_distance: settings?.routeplanner?.working_distance ?? "",
        },
    };
};

export default function RouteplannerSettingsFormFields({
    control,
    errors,
    setValue,
    defaultAppSettings,
}) {
    const resumeTrueLabel = "Resume from abort point";
    const resumeFalseLabel = "Restart from beginning";
    const resumeUndefinedLabel = "No behaviour defined";

    return (
        <>
            <h4>Route planner</h4>
            <SelectFormGroup
                label="Resume interrupted scan lines"
                name="settings.routeplanner.resume_interrupted_line_scan"
                control={control}
                errors={errors}
                setValue={setValue}
                defaultValue={defaultAppSettings ? "undefined" : undefined}
                options={[
                    {
                        id: "undefined",
                        name: defaultAppSettings
                            ? defaultAppSettings.routeplanner
                                  ?.resume_interrupted_line_scan != null
                                ? defaultAppSettings.routeplanner
                                      .resume_interrupted_line_scan
                                    ? `Use default: ${resumeTrueLabel}`
                                    : `Use default: ${resumeFalseLabel}`
                                : `Use default: ${resumeUndefinedLabel}`
                            : resumeUndefinedLabel,
                    },
                    { id: "true", name: resumeTrueLabel },
                    { id: "false", name: resumeFalseLabel },
                ]}
            />
            <TextFormGroup
                label="Horizontal overlap"
                name="settings.routeplanner.horizontal_overlap"
                control={control}
                errors={errors}
                setValue={setValue}
                defaultValue={defaultAppSettings ? "" : undefined}
                unitText="%"
                placeholder={
                    defaultAppSettings
                        ? defaultAppSettings?.routeplanner
                              ?.horizontal_overlap != null
                            ? `Leave empty to use default: ${defaultAppSettings?.routeplanner?.horizontal_overlap}`
                            : "Leave empty to ignore"
                        : undefined
                }
            />
            <TextFormGroup
                label="Vertical overlap"
                name="settings.routeplanner.vertical_overlap"
                control={control}
                errors={errors}
                setValue={setValue}
                defaultValue={defaultAppSettings ? "" : undefined}
                unitText="%"
                placeholder={
                    defaultAppSettings
                        ? defaultAppSettings?.routeplanner?.vertical_overlap !=
                          null
                            ? `Leave empty to use default: ${defaultAppSettings?.routeplanner?.vertical_overlap}`
                            : "Leave empty to ignore"
                        : undefined
                }
            />
            <TextFormGroup
                label="Working distance"
                name="settings.routeplanner.working_distance"
                control={control}
                errors={errors}
                setValue={setValue}
                defaultValue={defaultAppSettings ? "" : undefined}
                unitText="m"
                placeholder={
                    defaultAppSettings
                        ? defaultAppSettings?.routeplanner?.working_distance !=
                          null
                            ? `Leave empty to use default: ${defaultAppSettings.routeplanner.working_distance}`
                            : "Leave empty to ignore"
                        : undefined
                }
            />
        </>
    );
}
